import { initEngine, generateJSON, isWebGPUAvailable, checkWebGPU, clearModelCache, resetEngine } from "./webllmClient";
import { buildMentorMessages, DT_PHASES, buildMentorSchema, EMPTY_BRIEF, canAdvancePhase, mergeEvidenceSlots, resolveTeacherStage, openDecisionsForPhase, RAG_RELEVANCE_THRESHOLD } from "./mentorPrompt";
import { pickMove, classifyMove, MOVE_TYPES } from "./moveRouter";
import { pickMode, classifyMode } from "./modeRouter";
import { pickCheckpointReply, classifyCheckpointReply } from "./checkpointRouter";
import { classifyIntent, resolveIntentForMode } from "./intentRouter";
import { buildBrainstormMessages, parseBrainstormJSON } from "./brainstormPrompt";
import { buildSummarizeMessages, SUMMARIZE_SCHEMA, parseSummarize, bootstrapBrief, mergeCantiere, mergeFondamenta, mergeDecisions, EMPTY_CANTIERE, EMPTY_FONDAMENTA } from "./summarize";
import {
  buildScenarioActionMessages, buildScenarioActionSchema, extractValidNodeIds,
  parseScenarioProposal, readScenarioTreeState, decideScenarioAction,
} from "./scenarioActionPrompt";
import { userTurns } from "./chatHistory";
import { vocab } from "./prompts";
import { DEFAULT_SLM_MODEL } from "./models";
import { extractAndParseJSON } from "./jsonRepair";
import { API_BASE } from "../lib/apiBase";
import { captureUseCase, currentIdToken } from "../lib/adminNotify";

const RETRIEVE_URL = `${API_BASE}/retrieve`;

// Client-side running memory of keywords per session (mirrors backend
// SESSION_MEMORY, since the SLM path never touches the server flow).
const SESSION_MEMORY = {};

// Tracks which sessions have already fired the use-case capture call, so a
// long esplorazione_ideazione conversation doesn't re-POST every turn once
// problema_chiarito is filled. Server-side (POST /capture-use-case) is
// ALSO idempotent per session (see capture_use_case_if_new) — this Set is
// purely a client-side network-call reduction, not the source of truth.
const CAPTURED_USE_CASE_SESSIONS = new Set();

export function getWebGPUAvailable() {
  return isWebGPUAvailable();
}

// Async, reliable WebGPU probe (verifies an actual GPU adapter exists).
export { checkWebGPU, clearModelCache, resetEngine };

// Kick off (or reuse) the engine download. Safe to call repeatedly.
export function ensureEngine(modelId = DEFAULT_SLM_MODEL, onProgress) {
  return initEngine(modelId, onProgress);
}

// `lang` is sent along for the backend; retrieval itself is still over the
// single Italian collection. The signed-in user's ID token is attached when
// there is one: the backend only spends a Gemini call translating a
// non-Italian query for authenticated requests (anonymous retrieval still
// works, with the untranslated query).
async function authHeader() {
  try {
    const token = await currentIdToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch (e) {
    return {}; // no auth available — anonymous retrieve
  }
}

export async function fetchKnowledge(query, nResults = 3, lang = "it") {
  try {
    const res = await fetch(RETRIEVE_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...(await authHeader()) },
      body: JSON.stringify({ query, n_results: nResults, lang }),
    });
    if (!res.ok) return { text: "", minDistance: null };
    const data = await res.json();
    return { text: data.knowledge || "", minDistance: data.min_distance ?? null };
  } catch (e) {
    console.warn("RAG retrieve failed (continuing without knowledge):", e);
    return { text: "", minDistance: null };
  }
}

export function parseMentorJSON(raw, currentPhase, lang = "it") {
  const parsed = extractAndParseJSON(raw);
  if (parsed) return parsed;
  console.error("SLM JSON parse error (unrecoverable even after repair) — raw:", raw);
  return {
    message: (raw || "").trim() || vocab(lang).mentor_parse_fallback,
    phase: currentPhase,
    suggested_cards: [],
    evidences: [],
    // Explicit marker, not an inferred shape: finalizeMentorParsed must be
    // able to tell "the model produced nothing usable" from "the model
    // produced a valid but sparse JSON object" with certainty (a real
    // response can legitimately omit brief/evidence_slots/move_type too).
    // Consumed and deleted by finalizeMentorParsed, so it never reaches
    // the caller or the persisted session.
    __parseFallback: true,
  };
}

/**
 * Full in-browser mentor turn: RAG retrieve (server) → build prompt →
 * WebLLM generate → parse/validate → update session memory. Returns the
 * same object shape the backend /chat mentor response produces.
 */
export async function runSlmMentor(
  { sessionId, chat, purpose, usedCards = [], unusedCards = [], prevBrief = null, prevPhase = "esplorazione_ideazione", prevMode = null, prevModeAskAttempts = 0, prevPendingCheckpoint = false, prevTeacherStage = "osserva", scenarioTree = "", likelyBranchId = null, lang = "it" },
  { modelId = DEFAULT_SLM_MODEL, onProgress } = {}
) {
  await initEngine(modelId, onProgress);

  const generateShort = (prompt) => generateJSON([{ role: "user", content: prompt }], { maxTokens: 24 });
  // classifyIntent's prompt now asks for a short reasoning sentence before
  // the tag (see intentRouter.js) — 24 tokens isn't enough room for that.
  const generateClassifyIntent = (prompt) => generateJSON([{ role: "user", content: prompt }], { maxTokens: 150 });

  // No approach chosen yet: force "mentor" on EVERY turn until it resolves,
  // not just the first ask (mirrors the cloud IntentRouterNode's identical
  // guard/fix — gating on prevModeAskAttempts too only forced this on turn
  // 1; the user's actual reply to the mode question can easily misclassify
  // as brainstorm/chat and skip resolveSessionMode entirely).
  const rawIntent = (!prevMode)
    ? "mentor"
    : await classifyIntent({ chat, generate: generateClassifyIntent, lang });
  // A "brainstorm" misroute in mappatura/scenari_futuri would early-return
  // below and skip the whole mentor pipeline — including the scenario-action
  // pass, i.e. every fork and card for that turn. See resolveIntentForMode.
  const intent = resolveIntentForMode(rawIntent, prevMode);

  const decisionsForPhase = openDecisionsForPhase(prevBrief, prevPhase);
  const decisionsText = decisionsForPhase.map((d) => d.text).join("; ");

  if (intent === "brainstorm") {
    // Retrieval query against the Italian book chunks — internal, not
    // localized (" — decisioni:" included).
    let query = `Patterns and connections related to: ${purpose} and cards: ${usedCards.map((c) => c.label)}`;
    if (decisionsText) query += ` — decisioni: ${decisionsText}`;
    const { text: knowledge } = await fetchKnowledge(query, 3, lang);
    const { messages } = buildBrainstormMessages({ purpose, usedCards, unusedCards, knowledge, lang });
    const raw = await generateJSON(messages, {});
    return parseBrainstormJSON(raw);
  }

  const memory = SESSION_MEMORY[sessionId] || [];
  const currentPhase = prevPhase;

  let mode = prevMode;
  let modeAskAttempts = prevModeAskAttempts;
  let forceModeQuestion = false;
  let forceModeRedirect = false;
  let modeJustResolved = false;
  if (intent === "mentor") {
    ({ mode, modeAskAttempts, forceModeQuestion, forceModeRedirect, modeJustResolved } = await resolveSessionMode({
      chat, prevMode, prevModeAskAttempts, lang, generate: generateShort,
    }));
  }

  let forceCheckpoint = false;
  let advanceAllowed = false;
  let pendingCheckpoint = false;
  if (intent === "mentor" && !forceModeQuestion && !forceModeRedirect) {
    const canAdvanceNow = DT_PHASES.indexOf(currentPhase) < DT_PHASES.length - 1
      && canAdvancePhase(prevBrief?.evidence_slots, currentPhase);
    ({ forceCheckpoint, advanceAllowed, pendingCheckpoint } = await resolveCheckpoint({
      chat, prevPendingCheckpoint, canAdvance: canAdvanceNow, lang, generate: generateShort,
    }));
  }

  let move = "question";
  if (intent === "mentor" && !forceModeQuestion && !forceModeRedirect && !forceCheckpoint) {
    const { move_type: routed, needsLLM: moveNeedsLLM } = pickMove({ chat, lang });
    move = moveNeedsLLM ? await classifyMove({ chat, generate: generateShort, mode, lang }) : routed;
  }

  // teacherStageForPrompt must be known BEFORE the RAG fetch (only intervento
  // fetches at all) — resolveTeacherStage needs the mode/checkpoint flags
  // computed above, so this reorders the old fixed-per-turn fetch (which
  // used to run unconditionally right after intent classification) to here.
  const { teacherStageForPrompt } = resolveTeacherStage({
    prevTeacherStage, advanceAllowed, forceModeQuestion, forceModeRedirect, forceCheckpoint, modeJustResolved, intent,
  });

  let knowledge = "";
  let needsClarification = false;
  if (intent === "knowledge") {
    const turns = userTurns(chat);
    ({ text: knowledge } = await fetchKnowledge(turns[turns.length - 1] || "", 3, lang));
  } else if (intent === "mentor" && teacherStageForPrompt === "intervento") {
    // Only the intervento stage explains anything — osserva/applica/feedback
    // don't, so a fresh book lookup every turn was pure waste. Query is built
    // from the turn's actual need (obiettivo + the concrete dubbio + open
    // decisions' motivazioni) instead of a fixed "Design thinking {phase}"
    // string repeated every turn.
    const turns = userTurns(chat);
    const lastUserDubbio = turns[turns.length - 1] || "";
    const parts = [purpose, lastUserDubbio, decisionsText].filter(Boolean);
    const query = parts.length ? parts.join(" — ") : `Design thinking ${currentPhase}`;
    const { text, minDistance } = await fetchKnowledge(query, 3, lang);
    if (minDistance == null || minDistance > RAG_RELEVANCE_THRESHOLD) {
      needsClarification = true;
    } else {
      knowledge = text;
    }
  }
  // intent === "chat", or mentor outside intervento: no RAG fetch at all.

  const { messages, allLabels, wrapUpEligible, teacherStageOut, teacherStageIn } = buildMentorMessages({
    chat, purpose, usedCards, unusedCards, knowledge, memory,
    move, prevBrief: prevBrief || null, intent, prevPhase,
    mode, forceModeQuestion, forceModeRedirect, modeJustResolved, forceCheckpoint, advanceAllowed,
    prevTeacherStage,
    scenarioTree, likelyBranchId,
    needsClarification,
    lang,
  });

  // Strict schema = grammar constraint: forces the real keys and enum values so
  // the model can't parrot the template or run away repeating (see parseMentorJSON
  // failures with 0.5B). label enum only when cards exist (empty enum = invalid).
  const mentorSchema = buildMentorSchema(allLabels, mode);

  // The default 1024-token budget was observed truncating mid-response on a
  // turn with a full brief (evidenze_chiave + carte_collegate + a discorsivo
  // "direzione" field) — Italian prose runs more tokens/word than English,
  // and this response has the most fields of any call in the app. Grammar
  // constraint stops the model from producing a WRONG shape, but it can't
  // stop a cut-off mid-string when the budget runs out first.
  const raw = await generateJSON(messages, { schema: mentorSchema, maxTokens: 1536 });
  const parsed = parseMentorJSON(raw, currentPhase, lang);
  const finalized = finalizeMentorParsed(parsed, { sessionId, allLabels, currentPhase, prevBrief: prevBrief || null, intent, advanceAllowed, mode, forceModeQuestion, forceModeRedirect, modeJustResolved, wrapUpEligible, teacherStageOut, teacherStageIn });

  // Dedicated summarization pass (mentor turns only): a second focused call
  // produces the inferred objective + an incremental organic report, which
  // the mentor's own JSON no longer carries. Non-fatal — on failure the
  // previous objective/summary carry over (seeded below).
  finalized.brief.obiettivo = finalized.brief.obiettivo || prevBrief?.obiettivo || "";
  finalized.brief.sintesi_processo = finalized.brief.sintesi_processo || prevBrief?.sintesi_processo || "";
  // Carry the two-tier memory over unchanged by default — only a successful
  // summarize call below (mentor turns only) replaces these with a real
  // merge. A failed/skipped pass must never reset them to empty.
  finalized.brief.cantiere = prevBrief?.cantiere || EMPTY_CANTIERE;
  finalized.brief.fondamenta = prevBrief?.fondamenta || EMPTY_FONDAMENTA;
  finalized.brief.decisions = prevBrief?.decisions || [];
  if (intent === "mentor") {
    try {
      const turns = userTurns(chat);
      const lastUser = turns[turns.length - 1] || "";
      const { user_label: userLabel, mentor_label: mentorLabel } = vocab(lang);
      const exchange = `${userLabel}: ${lastUser}\n${mentorLabel}: ${finalized.message}`;
      const sumRaw = await generateJSON(
        buildSummarizeMessages({
          prevSummary: prevBrief?.sintesi_processo || "",
          prevObjective: prevBrief?.obiettivo || "",
          exchange,
          cards: usedCards,
          prevCantiere: prevBrief?.cantiere || null,
          prevFondamenta: prevBrief?.fondamenta || null,
          prevDecisions: prevBrief?.decisions || null,
          lang,
        }),
        { schema: SUMMARIZE_SCHEMA }
      );
      const { obiettivo, sintesi, cantiere, fondamenta, nuoveDecisioni, aggiornaDecisioni } = parseSummarize(
        sumRaw, { currentPhase, validPhases: DT_PHASES }
      );
      if (obiettivo) finalized.brief.obiettivo = obiettivo;
      if (sintesi) finalized.brief.sintesi_processo = sintesi;
      finalized.brief.cantiere = mergeCantiere(prevBrief?.cantiere, cantiere);
      finalized.brief.fondamenta = mergeFondamenta(prevBrief?.fondamenta, fondamenta);
      finalized.brief.decisions = mergeDecisions(prevBrief?.decisions, nuoveDecisioni, aggiornaDecisioni);
    } catch (e) {
      console.warn("summarize pass failed (non-fatal):", e);
    }
  }
  // Runs regardless of intent (not just "mentor"): the summarize LLM call is
  // reserved for mentor turns, but the free heuristic fallback must guarantee
  // a brief in any case, since on-device models are unreliable at intent
  // classification and a misrouted turn shouldn't leave the brief blank.
  bootstrapBrief(finalized.brief, purpose, lang);

  // Dedicated scenario-action pass (scenari_futuri mentor turns only): the
  // main call's own suggested_splits/suggested_cards proved unreliable in
  // practice — buried among 6 other concerns in one JSON, the model kept
  // either always forking root or never forking at all depending on how
  // much prose pressure was applied. A separate, single-purpose call (same
  // pattern as the summarize pass above) has nothing else to do, so it
  // can't deprioritize this decision. Overrides whatever the main call
  // produced for these two fields; leaves everything else (message, brief,
  // evidences, notes) untouched.
  // Skipped on the exact turn mode just resolved (modeJustResolved) — same
  // reason finalizeMentorParsed blanks suggested_cards/notes for that turn:
  // chat at that point is just the mode-selection exchange itself, with no
  // real content to fork on. Without this guard the model's only rich
  // textual anchor is the deck's own card list, and a weak model grabs two
  // card names as scenario_a/scenario_b — a fork "titled" after cards,
  // seconds into the session, before any discussion has happened.
  // console.log (never .info): .info gets hidden by the "Info" log-level
  // toggle in some devtools setups, which was silently swallowing every
  // line below — always print the gate decision itself first, so a turn
  // that skips this whole block (wrong intent/mode, or modeJustResolved)
  // is now visible too, instead of just going quiet.
  console.log(`[scenario-action:webllm] gate: intent=${intent} mode=${mode} modeJustResolved=${modeJustResolved}`
    + (intent === "mentor" && mode === "scenari_futuri" && !modeJustResolved ? " -> running" : " -> SKIPPED"));
  if (intent === "mentor" && mode === "scenari_futuri" && !modeJustResolved) {
    try {
      const validNodeIds = extractValidNodeIds(scenarioTree);
      const actionMessages = buildScenarioActionMessages({
        chat, scenarioTree, cardLabels: allLabels, likelyBranchId, lang,
      }).messages;
      console.log("[scenario-action:webllm] request sent to the local model:", actionMessages);
      const actionRaw = await generateJSON(actionMessages,
        { schema: buildScenarioActionSchema({ validNodeIds, cardLabels: allLabels }) });
      console.log("[scenario-action:webllm] raw response from the local model:", actionRaw);
      const { proposal, reason } = parseScenarioProposal(actionRaw, { validNodeIds, cardLabels: allLabels });
      const { fork, card, skipped } = decideScenarioAction(proposal, {
        treeState: readScenarioTreeState(scenarioTree),
        usedCardLabels: usedCards.map((c) => c.label),
      });
      finalized.suggested_splits = fork ? [fork] : [];
      finalized.suggested_cards = card ? [{ label: card, zone: currentPhase }] : [];
      console.log("[scenario-action:webllm] outcome:", { reason, proposal, fork, card, skipped });
    } catch (e) {
      // Leave whatever the main call itself produced for these two fields —
      // a transient failure here shouldn't blank out a turn that otherwise
      // succeeded.
      console.error("[scenario-action:webllm] threw (non-fatal, main call's own suggestions kept):", e);
    }
  }

  finalized.mode = mode;
  finalized.modeAskAttempts = modeAskAttempts;
  finalized.pendingCheckpoint = pendingCheckpoint;
  return finalized;
}

export function getSessionMemory(sessionId) {
  return SESSION_MEMORY[sessionId] || [];
}

// Validates/caps a model-returned brief, coercing invalid enum-like fields
// and falling back to the previous brief (or EMPTY_BRIEF) when the model's
// brief is missing or empty.
function validateBrief(brief, currentPhase, prevBrief) {
  // { ...spread } copy: callers mutate the returned brief in place (e.g.
  // setting obiettivo/sintesi_processo from the summarize pass). Returning
  // EMPTY_BRIEF itself would let that mutation stick to the shared module
  // singleton and leak into every other session whose first turn also falls
  // back to it.
  const fallback = prevBrief ? { ...prevBrief } : { ...EMPTY_BRIEF };
  if (!brief || typeof brief !== "object") return fallback;
  const obiettivo = typeof brief.obiettivo === "string" ? brief.obiettivo.trim() : "";
  const direzione = typeof brief.direzione === "string" ? brief.direzione.trim() : "";
  const sintesi_processo = typeof brief.sintesi_processo === "string" ? brief.sintesi_processo.trim() : "";
  const evidenze_chiave = (Array.isArray(brief.evidenze_chiave) ? brief.evidenze_chiave : [])
    .filter((e) => e && typeof e.text === "string" && e.text.trim())
    .map((e) => ({ phase: DT_PHASES.includes(e.phase) ? e.phase : currentPhase, text: e.text.trim() }))
    .slice(0, 8);
  const carte_collegate = (Array.isArray(brief.carte_collegate) ? brief.carte_collegate : [])
    .filter((c) => c && typeof c.label === "string" && c.label.trim())
    .map((c) => ({ label: c.label.trim(), by: c.by === "mentor" ? "mentor" : "utente",
                   perche: typeof c.perche === "string" ? c.perche.trim() : "" }))
    .slice(0, 10);
  const result = { obiettivo, evidenze_chiave, carte_collegate, direzione, sintesi_processo };
  const empty = !obiettivo && !direzione && !sintesi_processo && !evidenze_chiave.length && !carte_collegate.length;
  return empty ? fallback : result;
}

// Small models sometimes emit the message as a nested JSON string
// (`{"message":"..."}`) instead of plain text — and because that blob is then
// fed back into the next turn's history, the nesting escalates and the mentor
// appears to "repeat the same JSON answer". Unwrap it here (both display and
// history read parsed.message) so the loop can't form. Capped depth; a message
// that only happens to start with "{" but isn't wrapper JSON is left as-is.
function unwrapMessage(msg) {
  let text = typeof msg === "string" ? msg.trim() : "";
  for (let i = 0; i < 4 && text.startsWith("{"); i++) {
    let inner;
    try {
      inner = JSON.parse(text);
    } catch (e) {
      break; // not valid JSON — a real message that starts with a brace
    }
    if (inner && typeof inner.message === "string") {
      text = inner.message.trim();
    } else {
      break; // JSON, but not a message-wrapper — leave it
    }
  }
  return text;
}

// Validates the model's returned phase against prevPhase. advanceAllowed is
// only ever true for the one turn right after the user confirms a pending
// checkpoint (see resolveCheckpoint) — at that point the advance is
// mandatory, not left to the model's own judgment of whether the phase's
// "goal was reached". A weak/local model reliably keeps returning prevPhase
// on that turn regardless of the prompt's soft AVANZAMENTO FASE wording,
// which used to leave the phase stuck: canAdvancePhase's gate stays open
// forever once tripped (memory only grows), so the very next turn re-armed
// the SAME checkpoint question again — an infinite "sei pronto ad andare
// avanti?" loop the user never actually escaped by answering "sì". Forcing
// the one-step advance here (ignoring whatever phase value the model
// returned — including a bogus multi-step jump) is what breaks that loop:
// once confirmed, currentPhase actually changes, so the gate for the NEW
// phase starts closed again until fresh evidence reopens it.
function validatePhase(returnedPhase, prevPhase, evidenceSlots, intent, advanceAllowed = true) {
  if (intent !== "mentor") return prevPhase; // only mentor turns may advance
  const nextIdx = Math.min(DT_PHASES.indexOf(prevPhase) + 1, DT_PHASES.length - 1);
  const nextPhase = DT_PHASES[nextIdx];
  if (advanceAllowed && canAdvancePhase(evidenceSlots, prevPhase)) return nextPhase;
  return prevPhase;
}

// Shared mode-selection state machine for every local mentor path. Mirrors
// backend/pocketflow_agents.py's MentorGuideNode mode block exactly: ask
// once, then on each further turn while mode is still null try the
// rule-based pass then the LLM fallback; a failed attempt increments the
// counter and redirects; the 2nd failure defaults silently to
// "tradizionale". Once mode is set, only the rule-based pass is used to
// detect an explicit mid-session switch (no LLM call every turn).
//
// modeAskAttempts encodes BOTH "have we asked at all" and "how many
// classification attempts failed since asking" in one int, because the
// caller only round-trips this one field (no separate "hasAsked" flag
// exists in the data model): 0 = never asked (ask now, and set the
// returned value to 1 so the NEXT turn attempts classification instead of
// asking again — leaving it at 0 would re-ask forever, since the caller
// echoes the returned value back as next turn's prevModeAskAttempts with
// no other signal available to know an ask already happened). From then
// on, a returned value of N means "N-1 classification attempts have
// failed since the ask" — so the default-after-2-failures check compares
// the PRE-increment value against 2 (value 2 in means "1 prior failure",
// and this turn's second failure should default immediately, not
// increment to 3 and ask a third time).
export async function resolveSessionMode({ chat, prevMode, prevModeAskAttempts, generate, lang = "it" }) {
  let mode = prevMode || null;
  let modeAskAttempts = prevModeAskAttempts || 0;
  let forceModeQuestion = false;
  let forceModeRedirect = false;
  // True on the turn mode resolves from unset to a real value, AND on a
  // later mid-session explicit switch (see the `else` branch below). The
  // frontend applies the new layout when it receives this response, but
  // suggested_cards is dispatched before onMode (see Chatbot.jsx's
  // processApiResponse), so any cards suggested on this exact turn would
  // flash into the old layout an instant before being rearranged. The
  // caller suppresses suggestions for this one turn; the very next turn
  // already has the right layout in place from the start.
  let modeJustResolved = false;

  if (!mode) {
    if (modeAskAttempts === 0) {
      forceModeQuestion = true;
      modeAskAttempts = 1;
    } else {
      const ruleMode = pickMode({ chat, lang });
      const classified = ruleMode || await classifyMode({ chat, generate, lang });
      if (classified) {
        mode = classified;
        modeJustResolved = true;
      } else if (modeAskAttempts >= 2) {
        mode = "tradizionale";
        modeJustResolved = true;
      } else {
        modeAskAttempts += 1;
        forceModeRedirect = true;
      }
    }
  } else {
    const switched = pickMode({ chat, lang });
    if (switched && switched !== mode) {
      mode = switched;
      // A mid-session explicit switch is still a turn where the layout
      // changes under the response — same "flash into the old layout"
      // risk as the first resolution above, so suppress this turn too.
      modeJustResolved = true;
    }
  }

  return { mode, modeAskAttempts, forceModeQuestion, forceModeRedirect, modeJustResolved };
}

// Shared checkpoint-confirm state machine for every local mentor path.
// Mirrors backend/pocketflow_agents.py's MentorGuideNode checkpoint block
// exactly: when a checkpoint is pending, classify the reply (confirmed ->
// let the normal advance logic run this turn; rejected/unclear -> stay
// put, no forced re-ask); otherwise, if the evidence gate just opened,
// force a checkpoint turn instead of an immediate advance.
export async function resolveCheckpoint({ chat, prevPendingCheckpoint, canAdvance, generate, lang = "it" }) {
  let forceCheckpoint = false;
  let advanceAllowed = false;
  let pendingCheckpoint = false;

  if (prevPendingCheckpoint) {
    const rule = pickCheckpointReply({ chat, lang });
    const confirmed = rule !== null ? rule : await classifyCheckpointReply({ chat, generate, lang });
    if (confirmed) advanceAllowed = true;
  } else if (canAdvance) {
    forceCheckpoint = true;
    pendingCheckpoint = true;
  }

  return { forceCheckpoint, advanceAllowed, pendingCheckpoint };
}

// Shared post-processing for every local mentor path: card whitelist,
// evidence validation/dedup, session-memory fold, phase validation/gate,
// move_type coercion, brief validation/carry-over.
export function finalizeMentorParsed(parsed, { sessionId, allLabels, currentPhase, prevBrief, intent, advanceAllowed = true, mode = null, forceModeQuestion = false, forceModeRedirect = false, modeJustResolved = false, wrapUpEligible = false, teacherStageOut = "osserva", teacherStageIn = null }) {
  const cleaned = unwrapMessage(parsed.message);
  if (cleaned) parsed.message = cleaned;
  // Defense in depth for mentorPrompt.js's CONCLUSIONE instruction: a model
  // that ignores the "keep it false" note on a still-short conversation
  // can't wrap up the session prematurely — the gate is structural here,
  // not just prose.
  parsed.wrap_up = wrapUpEligible && parsed.wrap_up === true;
  parsed.offer_contact = parsed.wrap_up;
  // Defense in depth for mentorPrompt.js's card instruction: label must
  // exist in the deck, zone must match current/next phase, and — since the
  // card is meant to be the tip of an iceberg the mentor actually opens for
  // the user, not decorative — it must have been named in this turn's own
  // message. Capped at 1 for every mode.
  const nextPhaseIdx = Math.min(DT_PHASES.indexOf(currentPhase) + 1, DT_PHASES.length - 1);
  const nextPhase = DT_PHASES[nextPhaseIdx];
  const messageLower = (parsed.message || "").toLowerCase();
  parsed.suggested_cards = (parsed.suggested_cards || [])
    .filter((c) =>
      allLabels.includes(c.label)
      && (c.zone === currentPhase || c.zone === nextPhase)
      && messageLower.includes(c.label.toLowerCase())
    )
    .slice(0, 1);
  parsed.suggested_notes = (parsed.suggested_notes || [])
    .filter((n) => n && typeof n.text === "string" && n.text.trim())
    .map((n) => ({ phase: DT_PHASES.includes(n.phase) ? n.phase : currentPhase, text: n.text.trim() }))
    .slice(0, 2);
  // Defense in depth: the prompt already tells the model not to suggest
  // anything while objective/approach are still being settled, but force it
  // here too so a model that ignores the instruction can't still trigger a
  // board layout change before the user has even picked one. Also covers
  // the turn mode just resolved on (modeJustResolved) — see
  // resolveSessionMode's comment for why that turn needs it too.
  if (forceModeQuestion || forceModeRedirect || modeJustResolved) {
    parsed.suggested_cards = [];
    parsed.suggested_notes = [];
  }
  // Tells Chatbot.jsx to render the 3 mode-choice buttons under this
  // message instead of leaving the pick to free-text classification — see
  // resolveSessionMode's keyword-collision comment for why free text alone
  // is unreliable here.
  parsed.modeChoicePending = forceModeQuestion || forceModeRedirect;
  parsed.suggested_splits = mode === "scenari_futuri"
    ? (parsed.suggested_splits || [])
        .filter(s => s && typeof s.afterNodeId === "string" && s.afterNodeId.trim() && Array.isArray(s.branches))
        .map(s => ({
          afterNodeId: s.afterNodeId.trim(),
          branches: s.branches.filter(b => typeof b === "string" && b.trim()).slice(0, 4),
        }))
        .filter(s => s.branches.length > 0)
        .slice(0, 2)
    : [];
  const memory = getSessionMemory(sessionId);
  const seen = new Set(memory.map((e) => e.text.trim().toLowerCase()));
  const newEvidences = [];
  for (const e of parsed.evidences || []) {
    const text = (e.text || "").trim();
    if (!text || seen.has(text.toLowerCase())) continue;
    const phase = DT_PHASES.includes(e.phase) ? e.phase : currentPhase;
    seen.add(text.toLowerCase());
    newEvidences.push({ phase, text });
  }
  SESSION_MEMORY[sessionId] = [...memory, ...newEvidences];
  parsed.evidences = newEvidences;
  parsed.memory = SESSION_MEMORY[sessionId];
  parsed.phase = validatePhase(parsed.phase, currentPhase, prevBrief?.evidence_slots, intent, advanceAllowed);
  parsed.move_type = MOVE_TYPES.includes(parsed.move_type) ? parsed.move_type : "question";
  parsed.brief = validateBrief(parsed.brief, currentPhase, prevBrief || null);
  // Same "only mentor turns write teacher-loop state" guard validatePhase
  // applies above. evidence_slots is a REQUIRED grammar field on every turn
  // (Task 3/4 scoped it that way), so on a chat/knowledge turn the schema
  // forces the model to emit something even though the lightweight
  // ISTRUZIONI block never asked for anything meaningful there — merging
  // that into the permanent slot store would let a hallucination close a
  // phase gate. Python never hits this: IntentRouterNode keeps non-mentor
  // turns out of MentorGuideNode entirely. A null delta still normalizes
  // the shape (every DT_PHASES key present) without accepting new content.
  parsed.brief.evidence_slots = intent !== "mentor"
    ? mergeEvidenceSlots(prevBrief?.evidence_slots, null, currentPhase)
    : mergeEvidenceSlots(prevBrief?.evidence_slots, parsed.evidence_slots, currentPhase);
  if (intent === "mentor" && currentPhase === "esplorazione_ideazione" && !CAPTURED_USE_CASE_SESSIONS.has(sessionId)) {
    const problemText = (parsed.brief.evidence_slots[currentPhase]?.problema_chiarito || "").trim();
    if (problemText) {
      const sector = (parsed.brief.evidence_slots[currentPhase]?.settore || "").trim();
      captureUseCase(sessionId, problemText, sector).then((ok) => {
        if (ok) CAPTURED_USE_CASE_SESSIONS.add(sessionId);
      });
    }
  }
  // A turn whose response never parsed produced no teacher-stage-driven
  // answer, so it consumes no stage — hold where the cycle was. Mirrors
  // backend/pocketflow_agents.py's fallback returning teacher_stage.
  // teacherStageOut was computed by buildMentorMessages BEFORE the LLM
  // call, so it cannot know the response would come back garbled.
  const isParseFallback = parsed.__parseFallback === true;
  delete parsed.__parseFallback;
  parsed.teacherStage = (isParseFallback && teacherStageIn) ? teacherStageIn : teacherStageOut;
  return parsed;
}
